// packages/ui/src/forms/Form.tsx
'use client';

import type { ReactNode } from 'react';
import {
  FormProvider,
  type FieldErrors,
  type FieldValues,
  type SubmitHandler,
  type UseFormReturn,
} from 'react-hook-form';

import type { TFunc } from '@tourtask/types/i18n';
import type { AnyNamespaceTranslator } from './_i18n/FormI18nContext';
import { FormI18nProvider } from './_i18n/FormI18nContext';

// react-hook-form's `UseFormReturn<TInput, TContext, TOutput>` reserves
// a `TContext` slot we don't use; quarantine that single `any` here so
// callers don't have to.
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * The four keys of the leave-page guard, which `<GuardedForm>` renders (./GuardedForm.tsx). Every
 * translator handed to a form must accept them — all four exist in both the `common` and `public`
 * catalogs, and embed's `getStr` takes any key — so one translator type serves both forms.
 */
export type FormOwnKey =
  | 'If_you_leave_this_page_your_unsaved_changes_will_be_lost'
  | 'Leave_this_page_Q'
  | 'Leave_page'
  | 'Stay_on_page';

export type FormProps<
  TFieldValues extends FieldValues,
  TTransformedValues extends FieldValues = TFieldValues
> = {
  form: UseFormReturn<TFieldValues, any, TTransformedValues>;
  onSubmit: SubmitHandler<TTransformedValues>;
  /** Called when form validation fails. Use this to show an error dialog or snackbar. */
  onInvalid?: (errors: FieldErrors<TFieldValues>) => void;
  children: ReactNode;
  className?: string;

  /**
   * Translator function (e.g. next-intl's `t`). Fields use it to resolve schema error
   * message codes (`ERROR_REQUIRED`) into text.
   *
   * Typed at {@link FormOwnKey}, the leave-page guard's keys, for `<GuardedForm>`, which takes
   * this form's props. That is the contravariant form of "any translator that can do what I
   * need": a function accepting a SUPERSET of these keys is assignable, so a `common`
   * translator, a `public` one and embed's `getStr` all fit.
   */
  t: TFunc<FormOwnKey>;
  /**
   * When true, sets the `<form>` element's `noValidate` attribute so the
   * browser's HTML5 native validation is suppressed and submit is owned
   * entirely by RHF + the schema/resolver. Use this when the page wants
   * a single, uniform error UX (e.g. red borders on every empty
   * required field at once) rather than the browser's one-tooltip-at-
   * a-time flow blocking the submit before RHF can run.
   *
   * Off by default to preserve the existing browser-native fallback
   * for forms that haven't been audited for full schema-level required
   * coverage.
   */
  noValidate?: boolean;
  /**
   * Whether Enter in a field saves the form. Unset: only in a form of ONE field (a one-line dialog: a name, a code).
   * In a longer form, Enter in one field would save the others half filled (the owner, 2026-10-25: "since this dialog
   * is not a 1 liner, it should not save when pressing Enter. if it's a 1-liner, sure"). `true` where people expect
   * Enter to send more than one field: signing in (e-mail and password). A button pressed with Enter still saves.
   */
  submitOnEnter?: boolean;
};

/**
 * The fields of a form as a person sees them: inputs, text areas, selects and MUI's selects (a `combobox` that is no
 * input), without the hidden inputs MUI keeps behind its selects and pickers.
 */
const FORM_FIELDS_SELECTOR =
  'input:not([type="hidden"]):not([aria-hidden="true"]), textarea, select, [role="combobox"]:not(input)';

/**
 * A react-hook-form form: its element (submitted through RHF; Enter saves only a one-field form unless
 * `submitOnEnter`), RHF's provider and the translator its fields read. It never asks before the page is
 * left: a form that must is a `<GuardedForm>` (./GuardedForm.tsx, the backoffice's and the admin's
 * editors), so the widgets' forms carry no guard (docs/embed-size-and-speed.md §3.4 form-leave-guard).
 */
export function Form<
  TFieldValues extends FieldValues,
  TTransformedValues extends FieldValues = TFieldValues
>({
  form,
  onSubmit,
  onInvalid,
  children,
  className,
  t,
  noValidate = false,
  submitOnEnter,
}: FormProps<TFieldValues, TTransformedValues>) {
  const handleInvalid = (errors: FieldErrors<TFieldValues>) => {
    onInvalid?.(errors);
  };
  /* eslint-enable @typescript-eslint/no-explicit-any */

  return (
    <FormProvider {...form}>
      <FormI18nProvider t={t as AnyNamespaceTranslator}>
        {/* POST, not the browser's default GET: a form submitted before the page is ready (a tap on a slow
            phone) is submitted by the browser itself, and a GET puts every field in the address. A sign-in left
            its password in the browser's history and in the server's error log (2026-11-01). A POST that names
            no server action makes Next render the page again. */}
        <form
          method="post"
          noValidate={noValidate}
          onSubmit={(e) => { void form.handleSubmit(onSubmit, handleInvalid)(e); }}
          onKeyDown={(e) => {
            // The browser submits a form when Enter is pressed in one of its inputs (a text area takes it as a new
            // line, a button as its click). A field that used Enter itself (an autocomplete picking its option)
            // has prevented it already. Only this form's own inputs count: React bubbles a key press in a dialog opened
            // from inside the form (portalled, with a form of its own) up here too, and counting this form's fields
            // for it kept Enter from saving the dialog's one field (the booking's rental Edit price, 2026-10-27).
            if (e.key !== 'Enter' || e.defaultPrevented || e.nativeEvent.isComposing) return;
            if (!(e.target instanceof HTMLInputElement) || e.target.form !== e.currentTarget) return;
            const saves = submitOnEnter ?? e.currentTarget.querySelectorAll(FORM_FIELDS_SELECTOR).length <= 1;
            if (!saves) e.preventDefault();
          }}
          className={className}
        >
          {children}
        </form>
      </FormI18nProvider>
    </FormProvider>
  );
}
